import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import { MinimalTableCell, MinimalTableHeaderCell } from "src/ui/shared/table/MinimalTableComponents";
import { TableHeader } from "src/ui/shared/table/TableHeader";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning, SystemProperty } from "@dashboard/provisioning/types";
import { alternatingBg, Row, SectionDescription } from "@dashboard/shared/styles";
import { Paper, Table, TableBody, TableContainer, TableRow, Typography, useTheme } from "@mui/material";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";

const initialValues = { property: undefined, value: undefined, comment: null };

type FormValues = SystemProperty;

export const SystemProperties = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "SystemProperties");
    const theme = useTheme();
    const parentForm = useForm();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const properties = parentFormValues?.settings?.systemProperties || [];

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const newValue = { ...values, comment: values.comment || null };
        if (properties.length) {
            parentForm.change("settings.systemProperties", [...properties, newValue]);
        }
        if (!properties.length) {
            parentForm.change("settings.systemProperties", [newValue]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        if (!values.property) {
            errors.property = t("pages:provisioningPage.input.systemProperty.validation.empty");
        }
        if (!values.value) {
            errors.value = t("pages:provisioningPage.input.systemPropertyValue.validation.empty");
        }
        if (properties.includes(values)) {
            errors.property = t("pages:provisioningPage.input.systemProperty.validation.alreadyAdded");
        }
        return errors;
    };
    const handleDelete = (index: number) => {
        parentForm.change(
            "settings.systemProperties",
            properties.filter((_, i) => i !== index),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={value} onChange={onChange}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>
                            {t("pages:provisioningPage.section.systemProperties.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <SectionDescription>
                            {t("pages:provisioningPage.section.systemProperties.description")}
                        </SectionDescription>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("pages:provisioningPage.table.header.property")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("common:label.value")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">
                                                {t("common:label.comment")}
                                            </Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {properties.length ? (
                                        properties.map((item, index) => (
                                            <TableRow key={item.property + item.value} style={alternatingBg(index, theme)}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <Typography>{item.property}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.value}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.comment}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(index)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                        name="system-property-delete"
                                                    >
                                                        <i className="fas fa-trash-alt" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <MinimalTableCell align="center" colSpan={4}>
                                                <Typography>{t("pages:provisioningPage.table.noSystemProperties")}</Typography>
                                            </MinimalTableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Row style={{ paddingBottom: theme.spacing(3), marginTop: theme.spacing(4) }}>
                            <TextField
                                name="property"
                                label={t("pages:provisioningPage.input.systemProperty.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <TextField
                                name="value"
                                label={t("pages:provisioningPage.input.systemPropertyValue.caption")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                        </Row>
                        <Row>
                            <TextField
                                name="comment"
                                label={t("common:label.comment")}
                                disabled={!provisionings.length}
                                fullWidth
                            />
                            <PrimaryOutlinedButton
                                onClick={form.submit}
                                disabled={!provisionings.length}
                                name="system-property-add"
                            >
                                {t("common:button.add")}
                            </PrimaryOutlinedButton>
                        </Row>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
